import React, { useContext, useEffect, useState } from 'react';
import { Alert, Anchor, Badge, Button, Card, Divider, Group, Loader, Modal, NumberInput, Paper, Progress, SimpleGrid, Stack, Text, TextInput, Title, rem } from '@mantine/core';
import { IconCheck, IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { fromMantineDate } from '../helpers/mantineDates';
import { Timestamp, addDoc, collection, collectionGroup, deleteField, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { useFirestore, useFunctions } from '../helpers/firebaseContext';
import { MyUserDataContext } from '../contexts';
import { dateFormater, parseDayFirst } from '../helpers/common';
import { AddOrderGenevaResponseRequest, BaseResult, CustomClaims, Fund, Order, OrderStatusCode, SubscriptionType, SyncNavRequest } from '../types';
import OrderWindowsCalendar, { type OrderWindow } from './order_windows_calendar';
import { OpenEndedFundOrderPeriodSummary } from './open_ended_order_period_summary';
import { getNavStartAndEndDateOfWeek } from '../../../shared/geneva-functions';
import { GenevaIssueCode, describeGenevaIssue, validateProcessingGenevaResponses, validateWaitingForNavSubscription } from '../../../shared/orderProcessing';
import NavDateInput from './nav_date_input';
import FundNavCampaignSettings from './fund_nav_campaign_settings';

type MonitoringStatus = OrderStatusCode.waiting_for_nav | OrderStatusCode.processing;

type OrderIssue = {
    id: string;
    type: SubscriptionType;
    status: MonitoringStatus;
    issues: GenevaIssueCode[];
    genevaResponses?: Order["geneva_responses"];
};

interface FundOrderMonitoring {
    waiting: {
        total: number;
        subscriptionIssues: OrderIssue[];
    };
    processing: {
        total: number;
        completed: number;
        incomplete: OrderIssue[];
    };
}

const ORDER_TYPES: SubscriptionType[] = ["subscription", "redemption"];
const MAX_ISSUE_PREVIEW = 10;
const GENEVA_REPAIR_CONCURRENCY = 5;
const createDefaultOrderMonitoring = (): FundOrderMonitoring => ({
    waiting: { total: 0, subscriptionIssues: [] },
    processing: { total: 0, completed: 0, incomplete: [] },
});
type OrderWindowSummary = Record<MonitoringStatus, Partial<Record<SubscriptionType, OrderWindow>>>;
const createDefaultOrderWindows = (): OrderWindowSummary => ({
    waiting_for_nav: {},
    processing: {},
});

type GenevaResponseKey = keyof NonNullable<Order["geneva_responses"]>;
type GenevaRepairAction = {
    orderID: string;
    orderType: SubscriptionType;
    key: GenevaResponseKey;
    issues: GenevaIssueCode[];
};

const GENEVA_RESPONSE_LABELS: Record<GenevaResponseKey, string> = {
    initial_subscription_journal_transaction: "Initial Subscription JT",
    reversal_subscription_journal_transaction: "Reversal Subscription JT",
    subscription_journal_transaction: "Subscription JT",
    redemption_journal_transaction: "Redemption JT",
    gwi_order: "GWI Order",
};
const GENEVA_REPAIR_ISSUE_RESPONSE_MAP: Partial<Record<GenevaIssueCode, GenevaResponseKey>> = {
    missing_initial_subscription_journal_transaction: "initial_subscription_journal_transaction",
    initial_subscription_journal_transaction_not_success: "initial_subscription_journal_transaction",
    missing_reversal_subscription_journal_transaction: "reversal_subscription_journal_transaction",
    reversal_subscription_journal_transaction_not_success: "reversal_subscription_journal_transaction",
    missing_subscription_journal_transaction: "subscription_journal_transaction",
    subscription_journal_transaction_not_success: "subscription_journal_transaction",
    missing_redemption_journal_transaction: "redemption_journal_transaction",
    redemption_journal_transaction_not_success: "redemption_journal_transaction",
    missing_gwi_order: "gwi_order",
    gwi_order_not_success: "gwi_order",
};

const describeIssueWithStatusMessage = (issue: OrderIssue, issueCode: GenevaIssueCode) => {
    const baseDescription = describeGenevaIssue(issueCode);
    const responseKey = GENEVA_REPAIR_ISSUE_RESPONSE_MAP[issueCode];
    if (!responseKey) {
        return baseDescription;
    }
    const response = issue.genevaResponses?.[responseKey];
    const statusMessage = typeof response?.status_message === "string" ? response.status_message : undefined;
    return statusMessage ? `${baseDescription} (${statusMessage})` : baseDescription;
};

const buildBulkGenevaResponseRepairs = (issues: OrderIssue[]): GenevaRepairAction[] => {
    const repairs = new Map<string, GenevaRepairAction>();
    issues.forEach((issue) => {
        if (issue.id === "unknown") {
            return;
        }
        issue.issues.forEach((issueCode) => {
            const responseKey = GENEVA_REPAIR_ISSUE_RESPONSE_MAP[issueCode];
            if (!responseKey) {
                return;
            }
            const repairKey = `${issue.id}:${responseKey}`;
            const existingRepair = repairs.get(repairKey);
            if (existingRepair) {
                existingRepair.issues.push(issueCode);
            } else {
                repairs.set(repairKey, {
                    orderID: issue.id,
                    orderType: issue.type,
                    key: responseKey,
                    issues: [issueCode],
                });
            }
        });
    });
    return Array.from(repairs.values());
};

const runWithConcurrency = async <T,>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) => {
    for (let index = 0; index < items.length; index += concurrency) {
        await Promise.all(items.slice(index, index + concurrency).map(worker));
    }
};

const alignDateToNavDay = (seed: Date, navUpdateDay: number): Date => {
    const aligned = new Date(seed);
    aligned.setHours(0, 0, 0, 0);
    const diff = (navUpdateDay - aligned.getDay() + 7) % 7;
    aligned.setDate(aligned.getDate() + diff);
    return aligned;
};

const resolveNavDateForStatus = (
    status: MonitoringStatus,
    fundNavDate: Date,
    navUpdateDay: number
): Date => {
    if (status === OrderStatusCode.processing) {
        return new Date(fundNavDate);
    }
    const nextBasedOnFund = new Date(fundNavDate);
    nextBasedOnFund.setDate(nextBasedOnFund.getDate() + 7);
    let candidate = alignDateToNavDay(new Date(), navUpdateDay);
    if (candidate <= nextBasedOnFund) {
        candidate = nextBasedOnFund;
    }
    return candidate;
};

const FundManagementCard: React.FC<{ fund: Fund }> = ({ fund }) => {
    //NAV States
    const [nav, setNav] = useState<number | string | undefined>(fund.nav_per_unit);
    const [navDate, setNavDate] = useState<Date | null>(null);
    const [navLoading, setNavLoading] = useState<boolean>(false);
    const [syncNavLoading, setSyncNavLoading] = useState<boolean>(false);
    const [bulkGenevaRepairLoading, setBulkGenevaRepairLoading] = useState<boolean>(false);
    const [bulkGenevaRepairModalOpen, setBulkGenevaRepairModalOpen] = useState<boolean>(false);

    //Dividend States
    const [dividendPercentage, setDividendPercentage] = useState<number | string | undefined>();
    const [dividendDate, setDividendDate] = useState<Date | null>(null);
    const [dividendNotes, setDividendNotes] = useState<string>('');
    const [dividendLoading, setDividendLoading] = useState<boolean>(false);
    const [dividendModalOpen, setDividendModalOpen] = useState<boolean>(false);
    const [orderMonitoring, setOrderMonitoring] = useState<FundOrderMonitoring>(createDefaultOrderMonitoring);
    const [orderMonitoringLoading, setOrderMonitoringLoading] = useState(false);
    const [orderMonitoringError, setOrderMonitoringError] = useState<string | null>(null);
    const [orderMonitoringWarning, setOrderMonitoringWarning] = useState<string | null>(null);
    const [pdfProgress, setPdfProgress] = useState<{ total: number; processed: number; updatedAt: Date | null } | null>(null);

    const db = useFirestore();
    const functions = useFunctions();
    const appContext = useContext(MyUserDataContext);
    const navDateMillis = fund.nav_date ? fund.nav_date.toMillis() : null;
    const userClaims = appContext?.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;
    const canBulkRepairGenevaResponses = Boolean(userClaims?.admin || userClaims?.cr);

    const formatDateTime = (value: Date) => `${dateFormater(value)} ${value.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    const bulkGenevaRepairs = React.useMemo(
        () => buildBulkGenevaResponseRepairs([
            ...orderMonitoring.waiting.subscriptionIssues,
            ...orderMonitoring.processing.incomplete,
        ]),
        [orderMonitoring.waiting.subscriptionIssues, orderMonitoring.processing.incomplete],
    );
    const bulkGenevaRepairOrderCount = React.useMemo(
        () => new Set(bulkGenevaRepairs.map((repair) => repair.orderID)).size,
        [bulkGenevaRepairs],
    );

    useEffect(() => {
        setNav(fund.nav_per_unit);
    }, [fund.id, fund.nav_per_unit]);

    const orderWindowConfig = React.useMemo(() => {
        const summary = createDefaultOrderWindows();
        const warnings: string[] = [];
        const listenerConfigs: Array<{ status: MonitoringStatus }> = [];
        if (!fund.nav_date || typeof fund.nav_update_day_of_week !== "number") {
            return { summary, warnings, listenerConfigs };
        }
        const fundNavDateValue = fund.nav_date.toDate();
        const navUpdateDay = fund.nav_update_day_of_week;
        const statuses: MonitoringStatus[] = [OrderStatusCode.waiting_for_nav, OrderStatusCode.processing];
        for (const status of statuses) {
            listenerConfigs.push({ status });
            for (const type of ORDER_TYPES) {
                const cutoff = type === "subscription" ? fund.subscription_cutoff : fund.redemption_cutoff;
                if (!cutoff) {
                    warnings.push(`Missing ${type} cutoff configuration.`);
                    continue;
                }
                const navDateForStatus = resolveNavDateForStatus(status, fundNavDateValue, navUpdateDay);
                const { startOfWeek, endOfWeek } = getNavStartAndEndDateOfWeek(navDateForStatus, cutoff, navUpdateDay);
                summary[status][type] = {
                    start: new Date(startOfWeek),
                    end: new Date(endOfWeek),
                };
            }
        }
        return { summary, warnings, listenerConfigs };
    }, [
        fund.nav_update_day_of_week,
        navDateMillis,
        fund.subscription_cutoff?.daysBefore,
        fund.subscription_cutoff?.hour,
        fund.subscription_cutoff?.minute,
        fund.redemption_cutoff?.daysBefore,
        fund.redemption_cutoff?.hour,
        fund.redemption_cutoff?.minute,
    ]);

    useEffect(() => {
        if (!db) {
            return;
        }
        if (!fund.id || !fund.nav_date) {
            setOrderMonitoring(createDefaultOrderMonitoring());
            setOrderMonitoringError("Add a NAV date to monitor related orders.");
            setOrderMonitoringWarning(null);
            setOrderMonitoringLoading(false);
            return;
        }
        if (typeof fund.nav_update_day_of_week !== "number") {
            setOrderMonitoring(createDefaultOrderMonitoring());
            setOrderMonitoringError("Configure NAV update day of week to monitor orders.");
            setOrderMonitoringWarning(null);
            setOrderMonitoringLoading(false);
            return;
        }

        let cancelled = false;
        setOrderMonitoringLoading(true);
        setOrderMonitoringError(null);
        const baseWarnings = orderWindowConfig.warnings;
        const applyOrderMonitoringWarnings = (missingExpectedNavDateCount: number) => {
            if (cancelled) {
                return;
            }
            const warnings = [...baseWarnings];
            if (missingExpectedNavDateCount > 0) {
                warnings.push(`${missingExpectedNavDateCount} waiting order(s) missing expected_nav_date.`);
            }
            setOrderMonitoringWarning(warnings.length ? warnings.join(" ") : null);
        };
        applyOrderMonitoringWarnings(0);
        const statuses: MonitoringStatus[] = [OrderStatusCode.waiting_for_nav, OrderStatusCode.processing];
        const ordersByStatusAndType: Record<MonitoringStatus, Record<SubscriptionType, Order[]>> = {
            [OrderStatusCode.waiting_for_nav]: {
                subscription: [],
                redemption: [],
            },
            [OrderStatusCode.processing]: {
                subscription: [],
                redemption: [],
            },
        };
        const collectOrdersForStatus = (status: MonitoringStatus): Order[] =>
            ORDER_TYPES.flatMap((type) => ordersByStatusAndType[status][type]);

        const listenerCleanup: Array<() => void> = [];
        const listenerConfigs = orderWindowConfig.listenerConfigs;
        let expectedListenerCount = listenerConfigs.length;
        const initializedListeners = new Set<string>();
        const markListenerInitialized = (key: string) => {
            if (initializedListeners.has(key)) {
                return;
            }
            initializedListeners.add(key);
            if (initializedListeners.size === expectedListenerCount && !cancelled) {
                setOrderMonitoringLoading(false);
            }
        };

        const updateOrderMonitoringState = () => {
            if (cancelled) {
                return;
            }
            if (expectedListenerCount > 0 && initializedListeners.size < expectedListenerCount) {
                return;
            }
            const waitingOrdersAll = collectOrdersForStatus(OrderStatusCode.waiting_for_nav);
            const missingExpectedNavDateCount = waitingOrdersAll.filter(
                (order) => !(order.expected_nav_date instanceof Timestamp),
            ).length;
            const waitingOrders = navDateMillis
                ? waitingOrdersAll.filter(
                    (order) => order.expected_nav_date instanceof Timestamp && order.expected_nav_date.toMillis() > navDateMillis,
                )
                : waitingOrdersAll;
            const waitingIssues: OrderIssue[] = waitingOrders
                .filter((order) => order.type === "subscription")
                .map((order) => {
                    const validation = validateWaitingForNavSubscription(order);
                    return { order, validation };
                })
                .filter(({ validation }) => !validation.hasAllRequiredFields)
                .map(({ order, validation }) => ({
                    id: order.id ?? "unknown",
                    type: order.type as SubscriptionType,
                    status: OrderStatusCode.waiting_for_nav,
                    issues: validation.missingFields,
                    genevaResponses: order.geneva_responses,
                }));

            const processingOrders = collectOrdersForStatus(OrderStatusCode.processing);
            const processingEvaluations = processingOrders.map((order) => {
                const validation = validateProcessingGenevaResponses(order);
                return { order, validation };
            });
            const processingIncomplete: OrderIssue[] = processingEvaluations
                .filter(({ validation }) => !validation.hasAllRequiredFields)
                .map(({ order, validation }) => ({
                    id: order.id ?? "unknown",
                    type: order.type as SubscriptionType,
                    status: OrderStatusCode.processing,
                    issues: validation.missingFields,
                    genevaResponses: order.geneva_responses,
                }));

            setOrderMonitoring({
                waiting: {
                    total: waitingOrders.length,
                    subscriptionIssues: waitingIssues,
                },
                processing: {
                    total: processingOrders.length,
                    completed: processingOrders.length - processingIncomplete.length,
                    incomplete: processingIncomplete,
                },
            });
            applyOrderMonitoringWarnings(missingExpectedNavDateCount);
        };

        for (const { status } of listenerConfigs) {
            let ordersQuery = query(
                collectionGroup(db, "orders"),
                where("fund.id", "==", fund.id),
                where("tags.orderStatus.code", "==", status),
            );

            if (status === OrderStatusCode.processing) {
                if (fund.nav_date) {
                    ordersQuery = query(ordersQuery, where("nav_date", "==", fund.nav_date));
                }
            }
            else if (status === OrderStatusCode.waiting_for_nav) {
                if (fund.nav_date) {
                    ordersQuery = query(ordersQuery, where("expected_nav_date", ">", fund.nav_date));
                }
            }

            const listenerKey = `${status}`;
            const unsubscribe = onSnapshot(
                ordersQuery,
                (snapshot) => {
                    if (cancelled) {
                        return;
                    }
                    const buckets: Record<SubscriptionType, Order[]> = {
                        subscription: [],
                        redemption: [],
                    };
                    snapshot.forEach((docSnap) => {
                        const data = docSnap.data() as Order;
                        const orderWithId: Order = { ...data, id: data.id ?? docSnap.id };
                        buckets[orderWithId.type].push(orderWithId);
                    });
                    ordersByStatusAndType[status] = buckets;
                    markListenerInitialized(listenerKey);
                    updateOrderMonitoringState();
                },
                (error) => {
                    console.error("Failed to subscribe to fund order monitoring", error);
                    if (cancelled) {
                        return;
                    }
                    const message = error instanceof Error ? error.message : "Failed to load fund order monitoring data.";
                    setOrderMonitoring(createDefaultOrderMonitoring());
                    setOrderMonitoringError(message);
                    setOrderMonitoringLoading(false);
                },
            );
            listenerCleanup.push(unsubscribe);
        }

        if (!expectedListenerCount) {
            setOrderMonitoring(createDefaultOrderMonitoring());
            setOrderMonitoringLoading(false);
        }

        return () => {
            cancelled = true;
            listenerCleanup.forEach((unsubscribe) => unsubscribe());
        };
    }, [
        db,
        fund.id,
        fund.nav_update_day_of_week,
        navDateMillis,
        fund.subscription_cutoff?.daysBefore,
        fund.subscription_cutoff?.hour,
        fund.subscription_cutoff?.minute,
        fund.redemption_cutoff?.daysBefore,
        fund.redemption_cutoff?.hour,
        fund.redemption_cutoff?.minute,
        orderWindowConfig,
    ]);

    useEffect(() => {
        if (!db || !fund.id) {
            setPdfProgress(null);
            return;
        }
        const progressRef = doc(db, 'temp', 'pdf_generator');
        const unsubscribe = onSnapshot(
            progressRef,
            (snapshot) => {
                const data = snapshot.data() as { fundStatus?: Record<string, { total?: number; processed?: number; updatedAt?: Timestamp }> } | undefined;
                const status = data?.fundStatus?.[fund.id];
                if (status && typeof status.total === 'number') {
                    setPdfProgress({
                        total: status.total ?? 0,
                        processed: status.processed ?? 0,
                        updatedAt: status.updatedAt instanceof Timestamp ? status.updatedAt.toDate() : null,
                    });
                } else {
                    setPdfProgress(null);
                }
            },
            (error) => {
                console.error('Failed to subscribe to PDF progress', error);
                setPdfProgress(null);
            },
        );

        return () => {
            unsubscribe();
        };
    }, [db, fund.id]);

    const renderIssuesAlert = (issues: OrderIssue[], title: string, color: string) => {
        if (!issues.length) {
            return null;
        }

        return (
            <Alert mt="xs" color={color} variant="light" title={title} radius="sm">
                {issues.slice(0, MAX_ISSUE_PREVIEW).map((issue) => (
                    <Text key={`${issue.status}-${issue.id}`} size="xs">
                        {issue.id === "unknown" ? (
                            <>#{issue.id}</>
                        ) : (
                            <Anchor component={Link} to={`/orders/${issue.id}`} size="xs" target="_blank">
                                #{issue.id}
                            </Anchor>
                        )}{" "}
                        ({issue.type}): {issue.issues.map((issueCode) => describeIssueWithStatusMessage(issue, issueCode)).join(", ")}
                    </Text>
                ))}
                {issues.length > MAX_ISSUE_PREVIEW && (
                    <Text size="xs" c="dimmed" mt={4}>
                        +{issues.length - MAX_ISSUE_PREVIEW} more order(s) need attention.
                    </Text>
                )}
            </Alert>
        );
    };

    const handleSyncNav = async () => {
        if (!fund.id) {
            notifications.show({
                color: 'red',
                title: `No fund id for ${fund.name_en}`,
                message: 'Please add a fund id before syncing NAV.',
            });
            return;
        }
        setSyncNavLoading(true);
        const id = notifications.show({
            loading: true,
            title: `Syncing NAV for ${fund.name_en} (${fund.id})...`,
            message: 'Triggering NAV sync, hang tight.',
            autoClose: false,
            withCloseButton: false,
        });
        try {
            const syncNavCallable = httpsCallable<SyncNavRequest, BaseResult>(functions, "syncNav");
            await syncNavCallable({ fundCodes: [fund.id] });
            notifications.update({
                id,
                color: 'teal',
                title: `NAV sync requested for ${fund.name_en}`,
                message: 'Geneva sync is running in the background.',
                icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });
        } catch (e) {
            console.log(e);
            const errorMessage = e instanceof Error ? e.message : 'Please try again later.';
            notifications.update({
                id,
                color: 'red',
                title: `Failed to sync NAV for ${fund.name_en}`,
                message: errorMessage,
                icon: <IconX style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 3000,
            });
        } finally {
            setSyncNavLoading(false);
        }
    };

    const handleBulkGenevaRepair = async () => {
        if (!bulkGenevaRepairs.length) {
            return;
        }
        setBulkGenevaRepairLoading(true);
        const id = notifications.show({
            loading: true,
            title: `Repairing Geneva hand-off for ${fund.name_en}`,
            message: `${bulkGenevaRepairs.length} refresh request(s) across ${bulkGenevaRepairOrderCount} order(s).`,
            autoClose: false,
            withCloseButton: false,
        });

        const failures: Array<{ repair: GenevaRepairAction; message: string }> = [];
        try {
            const addOrderGenevaResponse = httpsCallable<AddOrderGenevaResponseRequest, BaseResult>(functions, 'addOrderGenevaResponse');
            await runWithConcurrency(bulkGenevaRepairs, GENEVA_REPAIR_CONCURRENCY, async (repair) => {
                try {
                    await addOrderGenevaResponse({ orderID: repair.orderID, key: repair.key });
                } catch (error) {
                    failures.push({
                        repair,
                        message: error instanceof Error ? error.message : 'Please try again later.',
                    });
                }
            });
            const repairedCount = bulkGenevaRepairs.length - failures.length;
            notifications.update({
                id,
                color: failures.length ? 'orange' : 'teal',
                title: failures.length ? `Geneva repair finished with ${failures.length} failure(s)` : `Geneva repair sent for ${fund.name_en}`,
                message: `${repairedCount} refresh request(s) sent.${failures.length ? ` ${failures[0].repair.orderID}: ${failures[0].message}` : ''}`,
                icon: failures.length ? <IconX style={{ width: rem(18), height: rem(18) }} /> : <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: failures.length ? 7000 : 2500,
            });
            if (!failures.length) {
                setBulkGenevaRepairModalOpen(false);
            }
        } catch (e) {
            console.error(e);
            const errorMessage = e instanceof Error ? e.message : 'Please try again later.';
            notifications.update({
                id,
                color: 'red',
                title: `Failed to repair Geneva hand-off for ${fund.name_en}`,
                message: errorMessage,
                icon: <IconX style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 5000,
            });
        } finally {
            setBulkGenevaRepairLoading(false);
        }
    };

    const updateNav = (fundID: string, nav_per_unit: number, nav_date: Timestamp) => {

        setNavLoading(true);
        const id = notifications.show({
            loading: true,
            title: 'Saving new NAV for ' + fundID + '...',
            message: 'Give it a minute...',
            autoClose: false,
            withCloseButton: false,
        });

        const fundRef = doc(db, "funds", fundID);
        updateDoc(fundRef, {
            "new_nav_awaiting_approval": {
                "nav_per_unit": nav_per_unit,
                "nav_date": nav_date,
                "updatedBy": appContext?.msal?.account?.username,
            }
        }).then(() => {
            setNavLoading(false);
            setNavDate(null);
            notifications.update({
                id,
                color: 'teal',
                title: 'Saved new NAV for ' + fundID,
                message: 'Nice!  It is now ' + nav_per_unit + '',
                icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });

        }).catch((e) => {
            setNavLoading(false);
            setNavDate(null);
            console.log(e);
            notifications.update({
                id,
                color: 'red',
                title: 'Failed to save new NAV for ' + fundID,
                message: e.message,
                icon: <IconX style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });

        });
    }
    const approveNav = (fundID: string, nav_per_unit: number, nav_date: Timestamp, updatedBy: string) => {

        const id = notifications.show({
            loading: true,
            title: 'Approving new NAV for ' + fundID + '...',
            message: 'Give it a minute...',
            autoClose: false,
            withCloseButton: false,
        });

        const fundRef = doc(db, "funds", fundID);
        updateDoc(fundRef, {
            "new_nav_awaiting_approval": deleteField(),
            "nav_per_unit": nav_per_unit,
            "nav_date": nav_date,
            "approvedBy": appContext?.msal?.account?.username,
            "updatedBy": appContext?.msal?.account?.username,

        }).then(() => {
            setNav(nav_per_unit);
            notifications.update({
                id,
                color: 'teal',
                title: 'Saved new NAV for ' + fundID,
                message: 'Nice!  It is now ' + nav_per_unit + '',
                icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });

        }).catch((e) => {
            console.log(e);
            notifications.update({
                id,
                color: 'red',
                title: 'Failed to save new NAV for ' + fundID,
                message: e.message,
                icon: <IconX style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });

        });
    }
    const addDividend = (fundID: string, percentage: number, dividendDate: Timestamp, notes?: string) => {
        setDividendLoading(true)
        const id = notifications.show({
            loading: true,
            title: 'Adding new dividend for ' + fundID + '...',
            message: 'Give it a minute...',
            autoClose: false,
            withCloseButton: false,
        });

        // Distributions are `type: 'dividend'` documents in the funds/{fundID}/updates
        // subcollection, not an array on the fund document. onFundUpdateWritten recomputes
        // MTD/YTD/ITD from this write.
        const newDividend: Record<string, unknown> = {
            type: "dividend",
            percentage: percentage,
            date: dividendDate,
            created_at: serverTimestamp(),
            created_by: appContext?.msal?.account?.username,
            updatedBy: appContext?.msal?.account?.username,
        };
        if (notes) {
            newDividend.notes = notes;
        }
        addDoc(collection(db, "funds", fundID, "updates"), newDividend).then(() => {
            setDividendLoading(false)
            setDividendPercentage('');
            setDividendDate(null);
            setDividendNotes('');
            setDividendModalOpen(false);
            notifications.update({
                id,
                color: 'teal',
                title: 'Added new Dividend for ' + fundID,
                message: 'Nice!  Latest Dividend is ' + percentage + '%',
                icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });

        }).catch((e) => {
            setDividendLoading(false)
            console.log(e);
            notifications.update({
                id,
                color: 'red',
                title: 'Failed to add dividend for ' + fundID,
                message: e.message,
                icon: <IconX style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 2000,
            });

        });
    }
    const openDividendModal = () => {
        setDividendPercentage('');
        setDividendDate(null);
        setDividendNotes('');
        setDividendModalOpen(true);
    };

    const closeDividendModal = () => {
        if (!dividendLoading) {
            setDividendModalOpen(false);
        }
    };

    return (
        <Card shadow="sm" radius="md" withBorder mb="xs" p="xs">
            <Group mb="xs" justify="space-between" align="center">
                <Group gap="xs">
                    <Title order={6}>{fund.name_en}</Title>
                    {/* <Badge color={fund.nav_update_state?.code.toLowerCase() === "done" ? "green" : "orange"}>
                        {fund.nav_update_state?.status}
                    </Badge> */}
                </Group>
                <Group gap="xs">
                    <FundNavCampaignSettings fund={fund} />
                    <Button
                        size="xs"
                        color="green"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={openDividendModal}
                    >
                        Add Dividend
                    </Button>
                    {canBulkRepairGenevaResponses && bulkGenevaRepairs.length > 0 && (
                        <Button
                            size="xs"
                            color="orange"
                            variant="light"
                            leftSection={<IconRefresh size={14} />}
                            loading={bulkGenevaRepairLoading}
                            disabled={orderMonitoringLoading}
                            onClick={() => setBulkGenevaRepairModalOpen(true)}
                        >
                            Repair Geneva ({bulkGenevaRepairs.length})
                        </Button>
                    )}
                    <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        leftSection={<IconRefresh size={14} />}
                        loading={syncNavLoading}
                        disabled={!fund.id}
                        onClick={handleSyncNav}
                    >
                        Sync NAV
                    </Button>
                </Group>
            </Group>
            <Group gap="xs" mb="xs" wrap="wrap">
                <Badge color="violet" variant="light" size="sm">
                    Waiting for NAV: {orderMonitoring.waiting.total}
                </Badge>
                <Badge color="orange" variant="light" size="sm">
                    Processing: {orderMonitoring.processing.total}
                </Badge>
                <Badge color="teal" variant="light" size="sm">
                    Geneva ready: {orderMonitoring.processing.completed}
                </Badge>
                <Badge color={orderMonitoring.waiting.subscriptionIssues.length + orderMonitoring.processing.incomplete.length > 0 ? "red" : "gray"} variant="light" size="sm">
                    Needs attention: {orderMonitoring.waiting.subscriptionIssues.length + orderMonitoring.processing.incomplete.length}
                </Badge>
                {orderMonitoringLoading && (
                    <Group gap={4} align="center">
                        <Loader size="xs" color="blue" />
                        <Text size="xs" c="dimmed">Refreshing orders…</Text>
                    </Group>
                )}
            </Group>
            {pdfProgress && pdfProgress.total > 0 && (
                <Card withBorder radius="sm" mb="xs" p="xs">
                    <Stack gap={4}>
                        <Group gap={6} justify="space-between" align="flex-end">
                            <Text size="xs" fw={600}>PDF Generation</Text>
                            <Text size="xs" c="dimmed">
                                {pdfProgress.processed}/{pdfProgress.total}
                            </Text>
                        </Group>
                        <Progress radius="xl" size="sm" value={Math.min(100, (pdfProgress.processed / pdfProgress.total) * 100)} />
                        {pdfProgress.updatedAt && (
                            <Text size="xs" c="dimmed">
                                Updated {formatDateTime(pdfProgress.updatedAt)}
                            </Text>
                        )}
                    </Stack>
                </Card>
            )}
            {orderMonitoringWarning && (
                <Alert color="yellow" variant="light" radius="sm" mb="xs">
                    {orderMonitoringWarning}
                </Alert>
            )}
            {orderMonitoringError && (
                <Alert color="red" variant="light" radius="sm" mb="xs">
                    {orderMonitoringError}
                </Alert>
            )}
            {renderIssuesAlert(orderMonitoring.waiting.subscriptionIssues, "Waiting for NAV issues", "red")}
            {renderIssuesAlert(orderMonitoring.processing.incomplete, "Processing Geneva hand-off issues", "orange")}
            <Divider mb="xs" />

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xs">
                <Card withBorder radius="sm" p="xs">
                    <Text size="xs" fw={600} mb={8}>Update NAV</Text>
                    <Group grow>
                        <NumberInput
                            size="xs"
                            label="NAV per unit"
                            placeholder="Enter NAV"
                            value={nav}
                            onChange={setNav}
                        />
                        <NavDateInput
                            size="xs"
                            label="NAV Date"
                            placeholder="Select date"
                            value={navDate}
                            navUpdateDayOfWeek={fund.nav_update_day_of_week}
                            onChange={(value) => {
                                if (value) {
                                    // Adjust date to UTC+3 timezone
                                    const utc3Date = new Date(value);
                                    utc3Date.setHours(utc3Date.getHours() + 3);
                                    setNavDate(utc3Date);
                                }
                            }}
                        />
                    </Group>
                    <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        fullWidth
                        mt={10}
                        disabled={!nav || !navDate || navLoading}
                        onClick={() => updateNav(fund.id, Number(nav), Timestamp.fromDate(navDate ?? new Date()))}
                    >
                        Update NAV
                    </Button>

                    <Divider my="xs" />

                    <Stack gap={6}>
                        <Text size="xs" fw={600}>NAV approval</Text>
                        {fund.new_nav_awaiting_approval ? (
                            <Paper withBorder radius="sm" p="xs" bg="var(--mantine-color-orange-0)">
                                <Stack gap={6}>
                                    <Text size="xs">NAV: {fund.new_nav_awaiting_approval.nav_per_unit}</Text>
                                    <Text size="xs">Date: {dateFormater(fund.new_nav_awaiting_approval.nav_date.toDate())}</Text>
                                    <Text size="xs" truncate>Submitted by: {fund.new_nav_awaiting_approval.updatedBy}</Text>
                                    <Button
                                        size="xs"
                                        variant="light"
                                        color="orange"
                                        mt={2}
                                        disabled={import.meta.env.VITE_ENV !== "development" &&
                                            fund.new_nav_awaiting_approval.updatedBy === appContext?.msal?.account?.username}
                                        onClick={() => {
                                            approveNav(
                                                fund.id,
                                                Number(fund.new_nav_awaiting_approval!.nav_per_unit),
                                                fund.new_nav_awaiting_approval!.nav_date,
                                                fund.new_nav_awaiting_approval!.updatedBy
                                            );
                                        }}
                                    >
                                        Approve
                                    </Button>
                                </Stack>
                            </Paper>
                        ) : (
                            <Text size="xs" c="dimmed">No pending NAV changes to approve.</Text>
                        )}
                    </Stack>

                    <Divider my="xs" />

                    <OpenEndedFundOrderPeriodSummary
                        fund={fund}
                        panelTitle="Current period orders"
                        cardMinHeight={0}
                    />
                </Card>

                <OrderWindowsCalendar
                    fund={fund}
                    waitingCount={orderMonitoring.waiting.total}
                    processingCount={orderMonitoring.processing.total}
                    weekendDays={[5, 6]}
                />
            </SimpleGrid>

            <Modal
                opened={bulkGenevaRepairModalOpen}
                onClose={() => {
                    if (!bulkGenevaRepairLoading) {
                        setBulkGenevaRepairModalOpen(false);
                    }
                }}
                title="Repair Geneva hand-off"
                centered
                size="lg"
            >
                <Stack gap="xs">
                    <Text size="sm">
                        This will run the same refresh action used on the order transaction view for {bulkGenevaRepairs.length} Geneva response(s).
                    </Text>
                    <Paper withBorder radius="sm" p="xs" mah={300} style={{ overflowY: 'auto' }}>
                        <Stack gap={6}>
                            {bulkGenevaRepairs.length ? bulkGenevaRepairs.map((repair) => (
                                <Group key={`${repair.orderID}-${repair.key}`} gap="xs" justify="space-between" align="flex-start">
                                    <Stack gap={2}>
                                        <Group gap={6}>
                                            <Anchor component={Link} to={`/orders/${repair.orderID}`} size="xs" target="_blank">
                                                #{repair.orderID}
                                            </Anchor>
                                            <Badge size="xs" variant="light">{repair.orderType}</Badge>
                                        </Group>
                                        <Text size="xs" fw={600}>{GENEVA_RESPONSE_LABELS[repair.key]}</Text>
                                        <Text size="xs" c="dimmed">
                                            {repair.issues.map((issueCode) => describeGenevaIssue(issueCode)).join(", ")}
                                        </Text>
                                    </Stack>
                                </Group>
                            )) : (
                                <Text size="xs" c="dimmed">No repairable Geneva hand-off issues.</Text>
                            )}
                        </Stack>
                    </Paper>
                    <Group justify="flex-end">
                        <Button
                            size="xs"
                            variant="default"
                            disabled={bulkGenevaRepairLoading}
                            onClick={() => setBulkGenevaRepairModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="xs"
                            color="orange"
                            loading={bulkGenevaRepairLoading}
                            disabled={!bulkGenevaRepairs.length}
                            leftSection={<IconRefresh size={14} />}
                            onClick={handleBulkGenevaRepair}
                        >
                            Repair
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={dividendModalOpen}
                onClose={closeDividendModal}
                title="Add dividend"
                centered
            >
                <Stack gap="xs">
                    <NumberInput
                        label="Dividend Percentage"
                        placeholder="Enter %"
                        value={dividendPercentage}
                        onChange={setDividendPercentage}
                        rightSection={<Text size="xs">%</Text>}
                    />
                    <DateInput
                        label="Date"
                        value={dividendDate}
                        firstDayOfWeek={0}
                        locale="en-gb"
                        dateParser={parseDayFirst}
                        placeholder="Select date"
                        valueFormat="DD/MM/YYYY"
                        onChange={(value) => {
                            if (value) {
                                setDividendDate(fromMantineDate(value)!);
                            }
                        }}
                    />
                    <TextInput
                        label="Notes"
                        placeholder="Optional notes"
                        value={dividendNotes}
                        onChange={(e) => setDividendNotes(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button
                            size="xs"
                            variant="default"
                            onClick={closeDividendModal}
                            disabled={dividendLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="xs"
                            color="green"
                            loading={dividendLoading}
                            disabled={!dividendPercentage || Number(dividendPercentage) <= 0 || !dividendDate}
                            onClick={() => addDividend(
                                fund.id,
                                Number(dividendPercentage),
                                Timestamp.fromDate(dividendDate ?? new Date()),
                                dividendNotes
                            )}
                        >
                            Save Dividend
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Card>
    );
};

export default FundManagementCard;
